import React, { useMemo, useState } from 'react'
import { Col, Container, Form, Modal as BootstrapModal, Row, Spinner } from 'react-bootstrap'
import { type TemplateLookup, type TemplateScope } from '../../../models/TemplateLibrary'
import { useGetTemplateReferencesQuery } from '../../../store/templateLibrary/templateLibrary'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { Modal } from '../../../components/Modal/Modal'
import { SelectDropdown } from '../../../components/SelectDropdown/SelectDropdown'
import { useReplacementOptions, type ReplacementOption } from './useReplacementOptions'
import { schedulingEmailDeleteCopy } from './SchedulingEmails/schedulingEmails'
import { isSchedulingEmailTemplate } from './TemplatesTab'

interface TemplateDeleteConfirmationModalProps {
  show: boolean
  loading: boolean
  onCancel: () => void
  onDelete: (replaceWithTemplateId?: string | null) => void
  templateValue: TemplateLookup
}

export interface DeleteModalCopy {
  title: string
  notInUse: string
  inUse: (referenceCount: number) => string
  replacementLabel: string
}

const defaultDeleteCopy = (templateValue: TemplateLookup): DeleteModalCopy => ({
  title: 'Delete template',
  notInUse: `The ${templateValue.name} template is not used in any meeting types. It can be safely deleted. This action can't be undone.`,
  inUse: (referenceCount) => `This template is used in ${referenceCount} meeting types. Choose a replacement before deleting.`,
  replacementLabel: 'Replace with',
})

export const TemplateDeleteConfirmationModal: React.FC<TemplateDeleteConfirmationModalProps> = ({ show, loading, onCancel, onDelete, templateValue }) => {
  const accountId = useAppSelector(selectAccountId)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const { data: templateReferences } = useGetTemplateReferencesQuery({ accountId, templateId: templateValue.id }, { refetchOnMountOrArgChange: true })
  const hasReferences = (templateReferences?.count ?? 0) > 0
  const copy = useMemo(
    () => (isSchedulingEmailTemplate(templateValue) ? schedulingEmailDeleteCopy(templateValue) : defaultDeleteCopy(templateValue)),
    [templateValue],
  )

  const scopeFilter: TemplateScope[] =
    templateValue.scope == 'system' || templateValue.scope == 'account' ? ['account', 'system'] : ['account', 'system', 'user']
  const { replacementOptions, defaultReplacement } = useReplacementOptions({
    templateValue,
    scopes: scopeFilter,
    createdBy: 'createdBy' in templateValue ? templateValue.createdBy : undefined,
    skip: !hasReferences,
  })
  const effectiveSelectedId = selectedTemplateId ?? defaultReplacement?.id ?? null

  const body = !templateReferences ? (
    <Spinner />
  ) : templateReferences.count === 0 ? (
    <HasNoReferenceTemplateDeleteModalBody text={copy.notInUse} />
  ) : (
    <HasReferenceTemplateDeleteModalBody
      text={copy.inUse(templateReferences.count)}
      replacementLabel={copy.replacementLabel}
      templateName={templateValue.name}
      replacementOptions={replacementOptions}
      defaultReplacement={defaultReplacement}
      onSelectTemplateId={setSelectedTemplateId}
    />
  )
  return (
    <Modal
      className='modal-lg'
      header={<BootstrapModal.Title>{copy.title}</BootstrapModal.Title>}
      body={body}
      show={show}
      onHide={onCancel}
      footerClassName='pt-0'
      footerPrimaryActionTitle='Delete template'
      footerPrimaryActionButtonVariant='danger'
      footerPrimaryActionLoading={loading}
      footerPrimaryActionDisabled={loading || !templateReferences || (hasReferences && !effectiveSelectedId)}
      showFooterActions={true}
      centered
      onFooterPrimaryAction={() => onDelete(effectiveSelectedId)}
      onFooterSecondaryAction={onCancel}
      headerBodySeparator={true}
    />
  )
}

interface TemplateDeleteModalBodyProps {
  text: string
}

const HasNoReferenceTemplateDeleteModalBody: React.FC<TemplateDeleteModalBodyProps> = ({ text }) => {
  return (
    <Container>
      <Row>
        <div>{text}</div>
      </Row>
    </Container>
  )
}

interface HasReferenceTemplateDeleteModalBodyProps extends TemplateDeleteModalBodyProps {
  replacementLabel: string
  templateName: string
  replacementOptions?: ReplacementOption[]
  defaultReplacement: ReplacementOption | null
  onSelectTemplateId: (templateId: string) => void
}

const HasReferenceTemplateDeleteModalBody: React.FC<HasReferenceTemplateDeleteModalBodyProps> = ({
  text,
  replacementLabel,
  templateName,
  replacementOptions,
  defaultReplacement,
  onSelectTemplateId,
}) => {
  return (
    <Container>
      <Row>
        <p>{text}</p>
      </Row>
      <Row>
        <Form.Label>Template to delete</Form.Label>
        <p>{templateName}</p>
      </Row>
      <Row>
        <Form.Label>{replacementLabel}</Form.Label>
        <Col>
          {replacementOptions ? (
            <SelectDropdown
              initialSelectedOption={defaultReplacement}
              options={replacementOptions}
              onOptionSelected={(option) => onSelectTemplateId(option.id)}
              placeholder='Select template'
            />
          ) : (
            <Spinner />
          )}
        </Col>
      </Row>
    </Container>
  )
}
